import { useCallback, useEffect, useState } from "react";
import { ADMIN_LEVEL_USE_MOCK } from "../config/admin.config";
import { levelApi } from "../api/levelApi";
import type { CatalogLevel1 } from "../types/level.types";
import type { LevelOption } from "../api/levelApi.types";
import { setLevel3LabelCache } from "../utils/level3LabelCache";

/** Keeps Class form level dropdowns in sync with Level Management store / API */
export function useClassCatalogOptions() {
  const [catalogLevels, setCatalogLevels] = useState<CatalogLevel1[]>([]);
  const [level2Options, setLevel2Options] = useState<LevelOption[]>([]);
  const [level3Options, setLevel3Options] = useState<LevelOption[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, l3] = await Promise.all([
        levelApi.getCatalogLevels(),
        levelApi.getLevel3Options(),
      ]);
      setCatalogLevels(catalog);
      setLevel3Options(l3);
      setLevel3LabelCache(l3);

      if (ADMIN_LEVEL_USE_MOCK) {
        setLevel2Options([]);
      } else {
        const l2 = await levelApi.getAllLevel2Options();
        setLevel2Options(l2);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshCatalog();

    const onFocus = () => void refreshCatalog();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refreshCatalog();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshCatalog]);

  return {
    catalogLevels,
    level2Options,
    level3Options,
    levelCascade: ADMIN_LEVEL_USE_MOCK,
    loading,
    refreshCatalog,
  };
}
